import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

// Initial state
const initialState = {
  currentTenant: null,
  tenantSettings: null,
  usage: null,
  isLoading: false,
  error: null
};

// Action types
const actionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_TENANT: 'SET_TENANT',
  SET_TENANT_SETTINGS: 'SET_TENANT_SETTINGS',
  SET_USAGE: 'SET_USAGE',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

// Reducer
const tenantReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SET_LOADING:
      return { ...state, isLoading: action.payload };

    case actionTypes.SET_TENANT:
      return { ...state, currentTenant: action.payload, isLoading: false };

    case actionTypes.SET_TENANT_SETTINGS:
      return { ...state, tenantSettings: action.payload };

    case actionTypes.SET_USAGE:
      return { ...state, usage: action.payload };

    case actionTypes.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };

    case actionTypes.CLEAR_ERROR:
      return { ...state, error: null };

    default:
      return state;
  }
};

// Create context
const TenantContext = createContext();

// Custom hook to use tenant context
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

// Tenant Provider component
export const TenantProvider = ({ children }) => {
  const [state, dispatch] = useReducer(tenantReducer, initialState);
  const { user, tenant: authTenant, isAuthenticated } = useAuth();

  // Load tenant data when user is authenticated
  useEffect(() => {
    if (isAuthenticated && authTenant) {
      dispatch({ type: actionTypes.SET_TENANT, payload: authTenant });
      loadTenantSettings();
      loadUsageData();
    }
  }, [isAuthenticated, authTenant]);

  // Load tenant settings
  const loadTenantSettings = async () => {
    try {
      const response = await axios.get('/api/tenant/settings');
      dispatch({ type: actionTypes.SET_TENANT_SETTINGS, payload: response.data });
    } catch (error) {
      console.error('Failed to load tenant settings:', error);
    }
  };

  // Load usage data
  const loadUsageData = async () => {
    try {
      const response = await axios.get('/api/tenant/usage');
      dispatch({ type: actionTypes.SET_USAGE, payload: response.data });
    } catch (error) {
      console.error('Failed to load usage data:', error);
    }
  };

  // Update tenant settings
  const updateTenantSettings = async (settings) => {
    dispatch({ type: actionTypes.SET_LOADING, payload: true });

    try {
      const response = await axios.put('/api/tenant/settings', settings);
      dispatch({ type: actionTypes.SET_TENANT_SETTINGS, payload: response.data });
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to update settings';
      dispatch({ type: actionTypes.SET_ERROR, payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  // Check if user has permission
  const hasPermission = (module, action) => {
    if (!user || !user.permissions) return false;

    // Super admin has all permissions
    if (user.role === 'superadmin') return true;

    // Check specific permission
    const permission = user.permissions.find(p => p.module === module);
    return permission && permission.actions.includes(action);
  };

  // Check subscription limits
  const checkLimit = (type) => {
    if (!state.currentTenant || !state.usage) return true;

    const limits = state.currentTenant.subscription;
    const current = state.usage;

    switch (type) {
      case 'users':
        return current.currentUsers < limits.maxUsers;
      case 'workers':
        return current.currentWorkers < limits.maxWorkers;
      case 'contractors':
        return current.currentContractors < limits.maxContractors;
      case 'storage':
        return current.storageUsed < limits.maxStorage;
      default:
        return true;
    }
  };

  // Get remaining limit
  const getRemainingLimit = (type) => {
    if (!state.currentTenant || !state.usage) return 0;

    const limits = state.currentTenant.subscription;
    const current = state.usage;

    switch (type) {
      case 'users':
        return Math.max(0, limits.maxUsers - current.currentUsers);
      case 'workers':
        return Math.max(0, limits.maxWorkers - current.currentWorkers);
      case 'contractors':
        return Math.max(0, limits.maxContractors - current.currentContractors);
      case 'storage':
        return Math.max(0, limits.maxStorage - current.storageUsed);
      default:
        return 0;
    }
  };

  const value = {
    ...state,
    loadTenantSettings,
    loadUsageData,
    updateTenantSettings,
    hasPermission,
    checkLimit,
    getRemainingLimit
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};